import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";

import { ContentLink } from "@/components/core/content-link";
import { LocaleSwitcher } from "@/components/core/locale-switcher";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/locale-context";
import { cn } from "@/lib/utils";
import type { ActionLink, NavigationItem } from "@/types/site";

interface NavbarProps {
  action?: ActionLink;
  items: NavigationItem[];
  siteName: string;
}

const navLinkClassName = ({ isActive }: { isActive: boolean }): string =>
  cn(
    "text-sm font-bold transition hover:text-foreground",
    isActive ? "text-foreground" : "text-spotify-silver",
  );

export function Navbar({ action, items, siteName }: NavbarProps): JSX.Element {
  const { localePath, t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.dataset.navOpen = String(isOpen);

    return () => {
      if (typeof document === "undefined") return;
      delete document.body.dataset.navOpen;
    };
  }, [isOpen]);

  return (
    <header className="sticky top-0 z-50 bg-spotify-dark/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link
          className="text-lg font-bold text-foreground"
          to={localePath("/")}
        >
          {siteName}
        </Link>
        <nav className="hidden items-center gap-6 md:flex" aria-label="Primary">
          {items.map((item) => (
            <NavLink
              end={item.href === "/"}
              key={item.href}
              className={navLinkClassName}
              to={localePath(item.href)}
            >
              {item.label}
            </NavLink>
          ))}
          {action ? (
            <ContentLink
              className="inline-flex min-h-10 items-center justify-center rounded-full-pill border border-spotify-border px-4 py-2 text-sm font-bold uppercase tracking-[1.4px] text-foreground transition hover:border-foreground/50 hover:text-primary"
              download={action.download}
              href={action.href}
            >
              {action.label}
            </ContentLink>
          ) : null}
          <LocaleSwitcher />
        </nav>
        <Button
          aria-controls="mobile-navigation"
          aria-expanded={isOpen}
          aria-label={isOpen ? t("actions.closeMenu") : t("actions.openMenu")}
          className="md:hidden"
          onClick={() => {
            setIsOpen((currentState) => !currentState);
          }}
          size="compact"
          variant="outline"
        >
          {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </Button>
      </div>
      <div
        className={cn(
          "overflow-hidden bg-spotify-dark px-6 transition-[max-height,opacity] duration-300 md:hidden",
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
        id="mobile-navigation"
      >
        <nav aria-label="Mobile" className="flex flex-col gap-2 py-4">
          {items.map((item) => (
            <NavLink
              end={item.href === "/"}
              key={item.href}
              className={({ isActive }) =>
                cn(
                  "rounded-full-pill px-3 py-3 text-sm font-bold transition",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-spotify-silver hover:bg-spotify-surface-light hover:text-foreground",
                )
              }
              onClick={() => {
                setIsOpen(false);
              }}
              to={localePath(item.href)}
            >
              {item.label}
            </NavLink>
          ))}
          {action ? (
            <ContentLink
              className="rounded-full-pill border border-spotify-border px-3 py-3 text-sm font-bold text-foreground"
              download={action.download}
              href={action.href}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              {action.label}
            </ContentLink>
          ) : null}
          <div className="pt-2">
            <LocaleSwitcher />
          </div>
        </nav>
      </div>
    </header>
  );
}
